/** CDN de smilies/peões (gs.xatblog + fallback gs.xat.com). */
export const GS_XATBLOG_BASE = 'https://gs.xatblog.net';
export const GS_XAT_COM_BASE = 'https://gs.xat.com';

/** Tamanhos suportados pelo CDN — qualquer outro valor não carrega. */
export const SMILEY_ALLOWED_SIZES = [20, 40, 80, 160] as const;

export type SmileyAllowedSizePx = (typeof SMILEY_ALLOWED_SIZES)[number];

export function normalizeSmileySizePx(
  sizePx: number,
  fallback: SmileyAllowedSizePx = 40
): SmileyAllowedSizePx {
  if (!Number.isFinite(sizePx) || sizePx <= 0) return fallback;

  if (SMILEY_ALLOWED_SIZES.includes(sizePx as SmileyAllowedSizePx)) {
    return sizePx as SmileyAllowedSizePx;
  }

  let nearest: SmileyAllowedSizePx = fallback;
  let minDiff = Number.POSITIVE_INFINITY;

  for (const allowed of SMILEY_ALLOWED_SIZES) {
    const diff = Math.abs(sizePx - allowed);
    if (diff < minDiff) {
      minDiff = diff;
      nearest = allowed;
    }
  }

  return nearest;
}

export function normalizeSmileyCode(code: string): string {
  const trimmed = code.trim();
  const inner = /^\(([^)]+)\)$/.exec(trimmed)?.[1]?.trim() ?? trimmed.replace(/^\(|\)$/g, '');
  return inner.toLowerCase();
}

/**
 * Path do CDN: no chat usa-se `#` (ex.: smile#r); na URL do gs vira `*` (smile*r).
 * Ver wiki xat / gs.xat.com — `%23` não renderiza combinações.
 */
export function encodeSmileyCodeForCdn(code: string): string {
  return normalizeSmileyCode(code).replace(/#/g, '*');
}

function gsPath(code: string, sizePx: number, suffix = ''): string {
  const px = normalizeSmileySizePx(sizePx);
  const normalized = encodeSmileyCodeForCdn(code);
  return `a_(${normalized})_${px}${suffix}`;
}

/** Strip animado — path após `/` no gs. */
export function smileyStripUrl(code: string, sizePx: number = 40): string {
  return `${GS_XATBLOG_BASE}/${gsPath(code, sizePx)}`;
}

/** Ícone de power no gs.xatblog.net (ex.: `a_(nome)_20?.png`). */
export function xatPowerIconUrl(code: string, sizePx: number = 40): string {
  return `${GS_XATBLOG_BASE}/${gsPath(code, sizePx, '?.png')}`;
}

export function xatPowerIconUrlPlain(code: string, sizePx: number = 40): string {
  return `${GS_XATBLOG_BASE}/${gsPath(code, sizePx)}`;
}

/** Fallback oficial (combos com efeitos costumam responder melhor aqui). */
export function xatComPowerIconUrl(code: string, sizePx: number = 40): string {
  return `${GS_XAT_COM_BASE}/${gsPath(code, sizePx, '?.png')}`;
}

export function xatComPowerIconUrlPlain(code: string, sizePx: number = 40): string {
  return `${GS_XAT_COM_BASE}/${gsPath(code, sizePx)}`;
}
